import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Search, Receipt, DollarSign, AlertTriangle, Edit, Trash2, Loader2, TrendingUp, Lock, Shuffle, Globe, BookOpen } from 'lucide-react';
import { FinancePageLayout } from '@/components/finance';
import { useRecurringExpenses, useRecurringExpensesSummary, useCreateRecurringExpense, useUpdateRecurringExpense, useDeleteRecurringExpense, type RecurringExpense } from '@/hooks/finance/useRecurringExpenses';
import { useLatestExchangeRate } from '@/hooks/finance/useExchangeRates';
import { useActiveChartOfAccounts } from '@/hooks/finance/useChartOfAccounts';
import { toast } from 'sonner';

const recurringExpenseSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório').max(100),
  description: z.string().optional(),
  vendor: z.string().optional(),
  business_unit: z.enum(['us', 'br', 'shared']).nullable().optional(),
  amount: z.coerce.number().min(0.01, 'Valor deve ser maior que zero'),
  currency: z.string().min(1, 'Moeda é obrigatória'),
  expense_type: z.enum(['fixed', 'variable', 'mixed']).default('fixed'),
  frequency: z.string().min(1, 'Frequência é obrigatória'),
  day_of_month: z.coerce.number().min(1).max(31).optional(),
  budget_amount: z.coerce.number().min(0).optional(),
  start_date: z.string().optional(),
  estimated_min: z.coerce.number().min(0).optional(),
  estimated_max: z.coerce.number().min(0).optional(),
  needs_invoice: z.boolean().default(false),
  account_code: z.string().optional(),
});

type RecurringExpenseFormData = z.infer<typeof recurringExpenseSchema>;

const formatCurrency = (value: number, currency: string = 'USD') => {
  return new Intl.NumberFormat(currency === 'BRL' ? 'pt-BR' : 'en-US', {
    style: 'currency',
    currency,
  }).format(value);
};

const FREQUENCY_LABELS: Record<string, string> = {
  daily: 'Diário',
  weekly: 'Semanal',
  monthly: 'Mensal',
  quarterly: 'Trimestral',
  semiannual: 'Semestral',
  annual: 'Anual',
};

type BusinessUnitFilter = 'all' | 'us' | 'br' | 'shared';

export default function RecurringExpenses() {
  const [searchTerm, setSearchTerm] = useState('');
  const [businessUnitFilter, setBusinessUnitFilter] = useState<BusinessUnitFilter>('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<RecurringExpense | null>(null);
  const [deletingExpense, setDeletingExpense] = useState<RecurringExpense | null>(null);
  
  const { data: expenses, isLoading } = useRecurringExpenses();
  const { data: summary } = useRecurringExpensesSummary();
  const { data: brlToUsdRate } = useLatestExchangeRate('BRL', 'USD');
  const { data: chartOfAccounts } = useActiveChartOfAccounts();
  
  const createExpense = useCreateRecurringExpense();
  const updateExpense = useUpdateRecurringExpense();
  const deleteExpense = useDeleteRecurringExpense();

  const form = useForm<RecurringExpenseFormData>({
    resolver: zodResolver(recurringExpenseSchema),
    defaultValues: {
      name: '',
      description: '',
      vendor: '',
      business_unit: undefined,
      amount: 0,
      currency: 'USD',
      expense_type: 'fixed',
      frequency: 'monthly',
      day_of_month: 1,
      budget_amount: undefined,
      start_date: '',
      estimated_min: undefined,
      estimated_max: undefined,
      needs_invoice: false,
      account_code: undefined,
    },
  });

  // Filtrar por business unit primeiro
  const buFilteredExpenses = expenses?.filter(exp => {
    if (businessUnitFilter === 'all') return true;
    if (businessUnitFilter === 'shared') return exp.business_unit === null;
    return exp.business_unit === businessUnitFilter;
  });

  // Filtrar por busca textual
  const filteredExpenses = buFilteredExpenses?.filter(exp => 
    exp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    exp.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    exp.vendor?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeExpenses = filteredExpenses?.filter(e => e.is_active) || [];

  // Calcular totais filtrados
  const filteredSummary = {
    count: activeExpenses.length,
    totalUSD: activeExpenses
      .filter(e => e.currency === 'USD')
      .reduce((sum, e) => sum + (e.amount || 0), 0),
    totalBRL: activeExpenses
      .filter(e => e.currency === 'BRL')
      .reduce((sum, e) => sum + (e.amount || 0), 0),
  };

  // Calcular total consolidado em USD
  const totalConsolidatedUSD = filteredSummary.totalUSD + 
    (brlToUsdRate?.rate ? filteredSummary.totalBRL * brlToUsdRate.rate : 0);

  const handleOpenDialog = (expense?: RecurringExpense) => {
    if (expense) {
      setEditingExpense(expense);
      form.reset({
        name: expense.name,
        description: expense.description || '',
        vendor: expense.vendor || '',
        business_unit: expense.business_unit,
        amount: expense.amount,
        currency: expense.currency,
        expense_type: expense.expense_type || 'fixed',
        frequency: expense.frequency,
        day_of_month: expense.day_of_month,
        budget_amount: expense.budget_amount || undefined,
        start_date: expense.start_date || '',
        estimated_min: expense.estimated_min || undefined,
        estimated_max: expense.estimated_max || undefined,
        needs_invoice: expense.needs_invoice || false,
        account_code: expense.account_code || undefined,
      });
    } else {
      setEditingExpense(null);
      form.reset({
        name: '',
        description: '',
        vendor: '',
        business_unit: undefined,
        amount: 0,
        currency: 'USD',
        expense_type: 'fixed',
        frequency: 'monthly',
        day_of_month: 1,
        budget_amount: undefined,
        start_date: '',
        estimated_min: undefined,
        estimated_max: undefined,
        needs_invoice: false,
        account_code: undefined,
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingExpense(null);
    form.reset();
  };

  const onSubmit = async (data: RecurringExpenseFormData) => {
    try {
      const businessUnitToSave = data.business_unit === 'shared' ? null : (data.business_unit || null);
      
      if (editingExpense) {
        await updateExpense.mutateAsync({
          id: editingExpense.id,
          ...data,
          business_unit: businessUnitToSave,
        });
        toast.success('Despesa atualizada com sucesso!');
      } else {
        await createExpense.mutateAsync({
          name: data.name,
          business_unit: businessUnitToSave,
          amount: data.amount,
          cost_type: data.expense_type === 'variable' ? 'variable' : 'fixed', // Derive cost_type from expense_type
          expense_type: data.expense_type,
          currency: data.currency,
          description: data.description,
          vendor: data.vendor,
          frequency: data.frequency,
          day_of_month: data.day_of_month,
          budget_amount: data.budget_amount,
          start_date: data.start_date,
          estimated_min: data.estimated_min,
          estimated_max: data.estimated_max,
          needs_invoice: data.needs_invoice,
        });
        toast.success('Despesa cadastrada com sucesso!');
      }
      handleCloseDialog();
    } catch (error: any) {
      console.error('Erro ao salvar despesa:', error);
      toast.error(error?.message || 'Erro ao salvar despesa. Verifique os dados e tente novamente.');
    }
  };

  const handleDelete = async () => {
    if (!deletingExpense) return;
    
    try {
      await deleteExpense.mutateAsync(deletingExpense.id);
      toast.success('Despesa desativada com sucesso!');
      setDeletingExpense(null);
    } catch (error: any) {
      console.error('Erro ao excluir despesa:', error);
      toast.error(error?.message || 'Erro ao excluir despesa.');
    }
  };

  const isSubmitting = createExpense.isPending || updateExpense.isPending;

  return (
    <FinancePageLayout>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Despesas Recorrentes</h1>

        <Dialog open={isDialogOpen} onOpenChange={(open) => open ? handleOpenDialog() : handleCloseDialog()}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Nova Despesa
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingExpense ? 'Editar Despesa Recorrente' : 'Adicionar Despesa Recorrente'}
              </DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nome *</FormLabel>
                        <FormControl>
                          <Input placeholder="Nome da despesa" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="vendor"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Fornecedor</FormLabel>
                        <FormControl>
                          <Input placeholder="Nome do fornecedor" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Descrição</FormLabel>
                        <FormControl>
                          <Input placeholder="Descrição breve" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="business_unit"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Business Unit</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || undefined}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="us">🇺🇸 USA</SelectItem>
                            <SelectItem value="br">🇧🇷 Brasil</SelectItem>
                            <SelectItem value="shared">🔗 Compartilhado</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="amount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Valor *</FormLabel>
                        <FormControl>
                          <Input type="number" step="0.01" placeholder="1000" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="currency"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Moeda *</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Moeda" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="USD">USD</SelectItem>
                            <SelectItem value="BRL">BRL</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="frequency"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Frequência *</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Frequência" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="daily">Diário</SelectItem>
                            <SelectItem value="weekly">Semanal</SelectItem>
                            <SelectItem value="monthly">Mensal</SelectItem>
                            <SelectItem value="quarterly">Trimestral</SelectItem>
                            <SelectItem value="semiannual">Semestral</SelectItem>
                            <SelectItem value="annual">Anual</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormDescription className="text-xs">
                          {field.value === 'daily' && '~30x/mês'}
                          {field.value === 'weekly' && '~4x/mês'}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                {/* Mostrar campo de dia apenas para frequências não-diárias */}
                {form.watch('frequency') !== 'daily' && form.watch('frequency') !== 'weekly' && (
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="day_of_month"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Dia do Pagamento *</FormLabel>
                          <FormControl>
                            <Input 
                              type="number" 
                              min={1} 
                              max={31} 
                              placeholder="15" 
                              {...field}
                              onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                            />
                          </FormControl>
                          <FormDescription className="text-xs">
                            Dia do mês em que a despesa vence
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="expense_type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tipo de Despesa *</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Tipo" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="fixed">
                              <div className="flex items-center gap-2">
                                <Lock className="h-3 w-3" />
                                Fixa
                              </div>
                            </SelectItem>
                            <SelectItem value="variable">
                              <div className="flex items-center gap-2">
                                <TrendingUp className="h-3 w-3" />
                                Variável
                              </div>
                            </SelectItem>
                            <SelectItem value="mixed">
                              <div className="flex items-center gap-2">
                                <Shuffle className="h-3 w-3" />
                                Mista
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormDescription className="text-xs">
                          {field.value === 'fixed' && 'Valor não muda'}
                          {field.value === 'variable' && 'Valor varia todo mês'}
                          {field.value === 'mixed' && 'Parte fixa + variável'}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="account_code"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Conta Contábil</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || undefined}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione a conta" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {chartOfAccounts?.map((account) => (
                              <SelectItem key={account.code} value={account.code}>
                                <div className="flex items-center gap-2">
                                  <BookOpen className="h-3 w-3 text-muted-foreground" />
                                  {account.code} - {account.name}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormDescription className="text-xs">
                          Vincular ao plano de contas
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="start_date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Data de Início</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                
                {/* Campos para despesas variáveis */}
                {(form.watch('expense_type') === 'variable' || form.watch('expense_type') === 'mixed') && (
                  <div className="border rounded-lg p-4 bg-muted/30 space-y-4">
                    <p className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      Configuração de Despesa Variável
                    </p>
                    <div className="grid grid-cols-3 gap-4">
                      <FormField
                        control={form.control}
                        name="estimated_min"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Valor Mínimo</FormLabel>
                            <FormControl>
                              <Input type="number" step="0.01" placeholder="500" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="estimated_max"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Valor Máximo</FormLabel>
                            <FormControl>
                              <Input type="number" step="0.01" placeholder="2000" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="budget_amount"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Orçamento Mensal</FormLabel>
                            <FormControl>
                              <Input type="number" step="0.01" placeholder="1200" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name="needs_invoice"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="cursor-pointer">
                              Aguardar fatura real
                            </FormLabel>
                            <FormDescription>
                              Gera lançamento com valor estimado e aguarda atualização quando a fatura chegar
                            </FormDescription>
                          </div>
                        </FormItem>
                      )}
                    />
                  </div>
                )}
                
                {/* Orçamento para despesas fixas */}
                {form.watch('expense_type') === 'fixed' && (
                  <FormField
                    control={form.control}
                    name="budget_amount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Orçamento Mensal</FormLabel>
                        <FormControl>
                          <Input type="number" step="0.01" placeholder="1200" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={handleCloseDialog}>
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {editingExpense ? 'Atualizar' : 'Salvar'}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card className="border-purple-200 bg-purple-50/50 dark:bg-purple-950/20">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Consolidado</CardTitle>
            <Globe className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {formatCurrency(totalConsolidatedUSD, 'USD')}
            </div>
            <p className="text-xs text-muted-foreground">
              USD + BRL convertido
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Mensal (USD)</CardTitle>
            <DollarSign className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(filteredSummary.totalUSD, 'USD')}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Mensal (BRL)</CardTitle>
            <DollarSign className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {formatCurrency(filteredSummary.totalBRL, 'BRL')}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Despesas Ativas</CardTitle>
            <Receipt className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{filteredSummary.count}</div>
          </CardContent>
        </Card>

        <Card className={summary?.overBudgetCount ? 'border-yellow-500' : ''}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Acima do Orçamento</CardTitle>
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {summary?.overBudgetCount || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <div className="flex gap-4 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar despesa..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={businessUnitFilter} onValueChange={(v) => setBusinessUnitFilter(v as BusinessUnitFilter)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Business Unit" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">🌐 Todos</SelectItem>
            <SelectItem value="us">🇺🇸 USA</SelectItem>
            <SelectItem value="br">🇧🇷 Brasil</SelectItem>
            <SelectItem value="shared">🔗 Compartilhado</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Fornecedor</TableHead>
                  <TableHead>BU</TableHead>
                  <TableHead>Conta</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead>Frequência</TableHead>
                  <TableHead>Dia</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeExpenses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-10 text-muted-foreground">
                      Nenhuma despesa encontrada
                    </TableCell>
                  </TableRow>
                ) : (
                  activeExpenses.map((expense) => {
                    const account = chartOfAccounts?.find(a => a.code === expense.account_code);
                    return (
                      <TableRow key={expense.id}>
                        <TableCell>
                          <div>
                            <span className="font-medium">{expense.name}</span>
                            {expense.description && (
                              <p className="text-xs text-muted-foreground truncate max-w-[200px]">{expense.description}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{expense.vendor || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {expense.business_unit === 'us' ? '🇺🇸 US' : expense.business_unit === 'br' ? '🇧🇷 BR' : '🌐 Shared'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {account ? (
                            <span className="text-xs text-muted-foreground">
                              {account.code}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground/50">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(expense.amount, expense.currency)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {FREQUENCY_LABELS[expense.frequency] || expense.frequency}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            Dia {expense.day_of_month || 1}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleOpenDialog(expense)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => setDeletingExpense(expense)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingExpense} onOpenChange={() => setDeletingExpense(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desativar Despesa</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja desativar "{deletingExpense?.name}"? 
              Esta ação irá marcar a despesa como inativa.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteExpense.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Desativar'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FinancePageLayout>
  );
}
